import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import { useProjects } from '@/hooks/use-projects';
import type { Project } from '@/lib/types';

function ProjectRow({ project, onClick }: { project: Project; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left flex items-center justify-between p-4 border-b last:border-b-0 hover:bg-muted/50 transition-colors"
    >
      <div className="min-w-0 flex-1">
        <p className="font-medium truncate">
          {project.project_number} — {project.name}
        </p>
        <p className="text-sm text-muted-foreground truncate">
          {project.client_name || 'No client'}
          {project.status ? ` · ${project.status.replace('_', ' ')}` : ''}
        </p>
      </div>
      <FileText className="h-5 w-5 text-muted-foreground shrink-0 ml-4" />
    </button>
  );
}

export function DailyReportsLandingPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useProjects({ page_size: 1000 });

  const sorted = useMemo(() => {
    const projects = data?.results ?? [];
    return [...projects].sort((a, b) => {
      const labelA = `${a.project_number} — ${a.name}`;
      const labelB = `${b.project_number} — ${b.name}`;
      return labelA.localeCompare(labelB);
    });
  }, [data]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-foreground flex items-center gap-2">
            <FileText className="h-6 w-6 text-blue-600" />
            Daily Reports
          </h1>
          <p className="text-gray-600 dark:text-muted-foreground mt-1">
            Select a project to view or create daily reports.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2">
          <CardTitle className="text-lg">Projects</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : sorted.length === 0 ? (
            <div className="p-8 text-center border border-dashed rounded-lg m-4">
              <p className="text-sm text-muted-foreground">No projects found.</p>
            </div>
          ) : (
            <div className="divide-y">
              {sorted.map((project) => (
                <ProjectRow
                  key={project.id}
                  project={project}
                  onClick={() => navigate(`/work/daily-reports/${project.id}`)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
